import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Loader2,
    ArrowLeft,
    Check,
    Upload,
    Image as ImageIcon,
    FileText,
    Globe,
    EyeOff,
    ExternalLink,
} from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import {
    useGetAdminBlogByIdQuery,
    useCreateBlogMutation,
    useUpdateBlogMutation,
    usePublishBlogMutation,
    useUnpublishBlogMutation,
    useUploadBlogCoverMutation,
    useUploadBlogDocumentMutation,
} from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import { resolveFileUrl } from '@/utils/fileUrl';
import { validateDocumentOnlyFile } from '@/utils/fileValidation';
import type { BlogContentType, CreateBlogRequest, UpdateBlogRequest } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'A';
}

const MAX_COVER_SIZE = 5 * 1024 * 1024; // 5 MB
function validateCoverImage(file: File): string | null {
    if (!file.type.startsWith('image/')) return 'Cover must be an image (JPG, PNG or WebP).';
    if (file.size > MAX_COVER_SIZE) return 'Cover image must be 5 MB or smaller.';
    return null;
}

export default function BlogEditor() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isNew = !id || id === 'new';
    const currentUser = useAppSelector(selectCurrentUser);

    const { data: post, isLoading: isLoadingPost } = useGetAdminBlogByIdQuery(id ?? '', { skip: isNew });

    const [createBlog, { isLoading: isCreating }] = useCreateBlogMutation();
    const [updateBlog, { isLoading: isUpdating }] = useUpdateBlogMutation();
    const [publishBlog, { isLoading: isPublishing }] = usePublishBlogMutation();
    const [unpublishBlog, { isLoading: isUnpublishing }] = useUnpublishBlogMutation();
    const [uploadCover, { isLoading: isUploadingCover }] = useUploadBlogCoverMutation();
    const [uploadDocument, { isLoading: isUploadingDoc }] = useUploadBlogDocumentMutation();

    const [title, setTitle] = useState('');
    const [contentType, setContentType] = useState<BlogContentType>('article');
    const [excerpt, setExcerpt] = useState('');
    const [body, setBody] = useState('');
    const [tagsInput, setTagsInput] = useState('');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    const coverInputRef = useRef<HTMLInputElement>(null);
    const docInputRef = useRef<HTMLInputElement>(null);

    // Seed the form from the server whenever the post loads/refreshes.
    useEffect(() => {
        if (!post) return;
        setTitle(post.title);
        setContentType(post.contentType);
        setExcerpt(post.excerpt ?? '');
        setBody(post.body ?? '');
        setTagsInput((post.tags ?? []).join(', '));
    }, [post]);

    const parseTags = (): string[] =>
        tagsInput
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);

    const flashSaved = () => {
        setSaved(true);
        window.setTimeout(() => setSaved(false), 2000);
    };

    // Create a new post, then move to its edit route (uploads/publish need an id).
    const handleCreate = async () => {
        setError('');
        if (!title.trim()) {
            setError('A title is required.');
            return;
        }
        try {
            const payload: CreateBlogRequest = { title: title.trim(), contentType };
            if (excerpt.trim()) payload.excerpt = excerpt.trim();
            if (contentType === 'article' && body.trim()) payload.body = body;
            const tags = parseTags();
            if (tags.length) payload.tags = tags;
            const created = await createBlog(payload).unwrap();
            navigate(`/admin/blog/${created.id}`, { replace: true });
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not create this post.'));
        }
    };

    const handleSave = async () => {
        if (isNew) return handleCreate();
        setError('');
        if (!title.trim()) {
            setError('A title is required.');
            return;
        }
        try {
            const data: UpdateBlogRequest = {
                title: title.trim(),
                contentType,
                excerpt: excerpt.trim(),
                tags: parseTags(),
            };
            if (contentType === 'article') data.body = body;
            await updateBlog({ id: id!, data }).unwrap();
            flashSaved();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not save your changes.'));
        }
    };

    const handlePublishToggle = async () => {
        if (isNew || !post) return;
        setError('');
        try {
            if (post.status === 'published') {
                await unpublishBlog(id!).unwrap();
            } else {
                // Persist edits first so publish validation sees the latest body/file.
                await handleSave();
                await publishBlog(id!).unwrap();
            }
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not update publish status.'));
        }
    };

    const handleCoverChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || isNew) return;
        const validationError = validateCoverImage(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        try {
            await uploadCover({ id: id!, file }).unwrap();
            flashSaved();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not upload the cover image.'));
        }
    };

    const handleDocChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || isNew) return;
        const validationError = validateDocumentOnlyFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        try {
            await uploadDocument({ id: id!, file }).unwrap();
            flashSaved();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not upload the document.'));
        }
    };

    const busy = isCreating || isUpdating;
    const coverUrl = resolveFileUrl(post?.coverImageUrl);
    const docUrl = resolveFileUrl(post?.documentUrl);
    const isPublished = post?.status === 'published';

    if (!isNew && isLoadingPost) {
        return (
            <main className="flex min-h-screen w-full items-center justify-center bg-[#EDEFF3]">
                <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
            </main>
        );
    }

    return (
        <main className="flex min-h-screen w-full flex-col bg-[#EDEFF3] px-4 py-6 md:px-6 lg:px-8 xl:px-12">
            <div className="flex flex-1 flex-col overflow-hidden rounded-3xl bg-white shadow-sm">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10">
                    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
                        {/* Header row */}
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/blog')}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#FF6B35]"
                            >
                                <ArrowLeft className="h-4 w-4" /> All posts
                            </button>

                            <div className="flex items-center gap-2">
                                {!isNew && isPublished && post && (
                                    <a
                                        href={`/blog/${post.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                                    >
                                        <ExternalLink className="h-4 w-4" /> View live
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-900 disabled:opacity-50"
                                >
                                    {busy ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : saved ? (
                                        <Check className="h-4 w-4" />
                                    ) : null}
                                    {isNew ? 'Create post' : saved ? 'Saved' : 'Save'}
                                </button>
                                {!isNew && (
                                    <button
                                        type="button"
                                        onClick={handlePublishToggle}
                                        disabled={isPublishing || isUnpublishing}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50 ${
                                            isPublished
                                                ? 'bg-amber-600 hover:bg-amber-700'
                                                : 'bg-[#FF6B35] hover:bg-[#ff5722]'
                                        }`}
                                    >
                                        {isPublishing || isUnpublishing ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : isPublished ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Globe className="h-4 w-4" />
                                        )}
                                        {isPublished ? 'Unpublish' : 'Publish'}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
                                {isNew ? 'New post' : 'Edit post'}
                            </h1>
                            {!isNew && post && (
                                <p className="mt-1 text-sm text-gray-500">
                                    <span
                                        className={`font-semibold ${isPublished ? 'text-green-600' : 'text-amber-600'}`}
                                    >
                                        {isPublished ? 'Published' : 'Draft'}
                                    </span>{' '}
                                    · slug{' '}
                                    <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{post.slug}</code>
                                </p>
                            )}
                        </div>

                        {error && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                            </div>
                        )}

                        {isNew && (
                            <p className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                                Save this post first, then you can upload a cover image
                                {contentType === 'document' ? ', attach the document file,' : ''} and publish it.
                            </p>
                        )}

                        {/* Content type */}
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-semibold text-gray-700">Content type</label>
                            <div className="flex gap-3">
                                {(['article', 'document'] as BlogContentType[]).map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setContentType(type)}
                                        className={`flex flex-1 items-center gap-2 rounded-xl border px-4 py-3 text-left text-sm transition-colors ${
                                            contentType === type
                                                ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-gray-900'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                        }`}
                                    >
                                        {type === 'article' ? (
                                            <FileText className="h-4 w-4 shrink-0" />
                                        ) : (
                                            <Upload className="h-4 w-4 shrink-0" />
                                        )}
                                        <span>
                                            <span className="block font-semibold capitalize">{type}</span>
                                            <span className="block text-xs text-gray-500">
                                                {type === 'article' ? 'Written body' : 'Downloadable file'}
                                            </span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Title */}
                        <div className="flex flex-col gap-2">
                            <label htmlFor="blog-title" className="text-sm font-semibold text-gray-700">
                                Title
                            </label>
                            <input
                                id="blog-title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={255}
                                placeholder="How background checks protect your business"
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-[#FF6B35]"
                            />
                        </div>

                        {/* Excerpt */}
                        <div className="flex flex-col gap-2">
                            <label htmlFor="blog-excerpt" className="text-sm font-semibold text-gray-700">
                                Excerpt <span className="font-normal text-gray-400">(optional, max 500)</span>
                            </label>
                            <textarea
                                id="blog-excerpt"
                                value={excerpt}
                                onChange={(e) => setExcerpt(e.target.value)}
                                maxLength={500}
                                rows={2}
                                placeholder="A short summary shown in listings and previews."
                                className="w-full resize-y rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-[#FF6B35]"
                            />
                        </div>

                        {/* Tags */}
                        <div className="flex flex-col gap-2">
                            <label htmlFor="blog-tags" className="text-sm font-semibold text-gray-700">
                                Tags <span className="font-normal text-gray-400">(comma separated)</span>
                            </label>
                            <input
                                id="blog-tags"
                                type="text"
                                value={tagsInput}
                                onChange={(e) => setTagsInput(e.target.value)}
                                placeholder="compliance, hiring"
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-[#FF6B35]"
                            />
                        </div>

                        {/* Body (article only) */}
                        {contentType === 'article' && (
                            <div className="flex flex-col gap-2">
                                <label htmlFor="blog-body" className="text-sm font-semibold text-gray-700">
                                    Body <span className="font-normal text-gray-400">(HTML)</span>
                                </label>
                                <textarea
                                    id="blog-body"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={16}
                                    placeholder="<h2>Section heading</h2>&#10;<p>Write your article here. Basic HTML tags are supported.</p>"
                                    className="w-full resize-y rounded-xl border border-gray-200 bg-white px-4 py-3 font-mono text-sm text-gray-900 outline-none focus:border-[#FF6B35]"
                                />
                                <p className="text-xs text-gray-400">
                                    A post cannot be published until an article has body content.
                                </p>
                            </div>
                        )}

                        {/* Uploads — only available once the post exists */}
                        {!isNew && (
                            <div className="flex flex-col gap-5 rounded-2xl border border-gray-100 bg-gray-50 p-5">
                                <h2 className="text-sm font-semibold text-gray-700">Media</h2>

                                {/* Cover image */}
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                                            {coverUrl ? (
                                                <img
                                                    src={coverUrl}
                                                    alt="Cover"
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <ImageIcon className="h-5 w-5 text-gray-300" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">Cover image</p>
                                            <p className="text-xs text-gray-500">JPG, PNG or WebP · up to 5 MB</p>
                                        </div>
                                    </div>
                                    <input
                                        ref={coverInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleCoverChange}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => coverInputRef.current?.click()}
                                        disabled={isUploadingCover}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {isUploadingCover ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Upload className="h-4 w-4" />
                                        )}
                                        {coverUrl ? 'Replace' : 'Upload'}
                                    </button>
                                </div>

                                {/* Document (document posts only) */}
                                {contentType === 'document' && (
                                    <div className="flex flex-col gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#FF6B35]/10">
                                                <FileText className="h-5 w-5 text-[#FF6B35]" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-800">
                                                    {post?.documentName ?? 'Document file'}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {post?.documentUrl ? (
                                                        <a
                                                            href={docUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-[#FF6B35] hover:underline"
                                                        >
                                                            View current file
                                                        </a>
                                                    ) : (
                                                        'PDF or Word · required before publishing'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <input
                                            ref={docInputRef}
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            onChange={handleDocChange}
                                            className="hidden"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => docInputRef.current?.click()}
                                            disabled={isUploadingDoc}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {isUploadingDoc ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Upload className="h-4 w-4" />
                                            )}
                                            {post?.documentUrl ? 'Replace' : 'Upload'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
